import { Box, ButtonPrimitive, Heading, Text } from '@kiwicom/orbit-components';
import { ErrorScreen } from '@kiwicom/account-components';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '@kiwicom/nitro/lib/services/auth/context';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { events, useLog, useLogOnMount } from '@kiwicom/account-tracking';
import { useFeature } from '@kiwicom/darwin';
import { captureMutationErrors, graphql, useFragment, useMutation } from '@kiwicom/account-relay';
import { formatCurrency } from '@kiwicom/account-homepage/src/ReferFriend/utils';

import TimelineComponent from '../Timeline/Timeline';
import AustralianUserErrorScreen from '../error/AustralianUserErrorScreen';
import ReferFriendTokenErrorScreen from '../error/ReferFriendTokenErrorScreen';
import type { ReferFriendIntroMutation } from './__generated__/ReferFriendIntroMutation.graphql';
import usePublishRefereeInvitationScreenDisplayed from '../../../services/events/business/refer-friend/usePublishRefereeInvitationScreenDisplayed';
import usePublishRefereeErrorScreenDisplayed from '../../../services/events/business/refer-friend/usePublishRefereeErrorScreenDisplayed';

const ReferFriendIntro = ({ root }) => {
  useLogOnMount(events.referFriend.VISIT_LANDING_PAGE);
  const { onOpenModal } = useAuth();
  const { log } = useLog();

  const [signInFulfilled, setSignInFulfilled] = useState(false);
  const [submitInviteTokenError, setSubmitInviteTokenError] = useState(null);
  const isAustralianUser = useFeature('DISABLE_REFER_A_FRIEND');
  const {
    query: { language, token },
    push,
  } = useRouter();
  const { locale } = useIntl();
  const publishRefereeInvitationScreenDisplayed = usePublishRefereeInvitationScreenDisplayed();
  const publishRefereeErrorScreenDisplayed = usePublishRefereeErrorScreenDisplayed();

  // router query can return string or string[]
  const linkingToken = Array.isArray(token) ? token.join(',')[0] : token;
  const queryLanguage = Array.isArray(language) ? language.join(',')[0] : language;

  const ReferFriendIntroFragment = graphql`
    fragment ReferFriendIntro_data on RootQuery
    @argumentDefinitions(token: { type: "String!" }, currencyId: { type: "String!" }) {
      referFriendTokenValidation(token: $token) {
        ... on ReferFriendTokenValidation {
          isValid
        }
        ... on ExpiredTokenError {
          __typename
        }
        ... on GeneralError {
          __typename
        }
        ... on ReferFriendNotEligibleError {
          __typename
        }
        ... on ReferralTokenOwnerError {
          __typename
        }
        ... on ReferralTokenAccountHasBookingsError {
          __typename
        }
        ... on ReferralTokenAccountAgeError {
          __typename
        }
      }

      referFriendPromoValues(currencyId: $currencyId) {
        firstBookingWithinDays
        maximumRefererCreditTotal {
          amount
          currency {
            format {
              format
            }
            character
            code
          }
        }
        refereeVoucherAmount {
          amount
          currency {
            format {
              format
            }
            character
            code
          }
        }
      }
    }
  `;
  const { referFriendTokenValidation, referFriendPromoValues } = useFragment(
    ReferFriendIntroFragment,
    root,
  );

  const { refereeVoucherAmount, maximumRefererCreditTotal, firstBookingWithinDays } =
    referFriendPromoValues;

  // Decide what kind of error to show (if any) when validating token on component mount/render
  const referFriendTokenValidationErrorType = referFriendTokenValidation.__typename;
  const isReferFriendTokenInValid =
    referFriendTokenValidationErrorType === 'ExpiredTokenError' ||
    referFriendTokenValidationErrorType === 'ReferralTokenOwnerError' ||
    referFriendTokenValidationErrorType === 'ReferFriendNotEligibleError' ||
    referFriendTokenValidationErrorType === 'LinkDoesNotExistError' ||
    referFriendTokenValidationErrorType === 'ReferralTokenAccountHasBookingsError' ||
    referFriendTokenValidationErrorType === 'ReferralTokenAccountAgeError';
  const isGeneralError =
    referFriendTokenValidationErrorType === 'GeneralError' ||
    submitInviteTokenError === 'GeneralError';

  const [submitReferralToken] = useMutation<ReferFriendIntroMutation>(graphql`
    mutation ReferFriendIntroMutation($input: SubmitReferralTokenInput!) {
      submitReferralToken(input: $input) {
        __typename
        ... on SubmitReferralTokenResponse {
          success
        }
        ... on ExpiredTokenError {
          message
        }
        ... on GeneralError {
          message
        }
        ... on ReferFriendNotEligibleError {
          message
        }
        ... on ReferralTokenOwnerError {
          message
        }
        ... on ReferralTokenAccountHasBookingsError {
          __typename
        }
        ... on ReferralTokenAccountAgeError {
          __typename
        }
      }
    }
  `);

  const submitInviteToken = useCallback(() => {
    submitReferralToken({
      variables: {
        input: {
          linkingToken,
        },
      },
      onCompleted: (response, errors) => {
        if (
          response.submitReferralToken?.__typename === 'SubmitReferralTokenResponse' &&
          response.submitReferralToken?.success
        ) {
          // signInFulfilled means user created account and signed-in on RAF page
          if (signInFulfilled) {
            void push(
              `/[language]/user/refer-friend/[token]/success?promo_code_amount=${
                refereeVoucherAmount.amount
              }&promo_code_currency=${
                refereeVoucherAmount.currency.code
              }&promo_code_value=${formatCurrency(refereeVoucherAmount, locale)}`,
              `/${queryLanguage}/user/refer-friend/${linkingToken}/success?promo_code_amount=${
                refereeVoucherAmount.amount
              }&promo_code_currency=${
                refereeVoucherAmount.currency.code
              }&promo_code_value=${formatCurrency(refereeVoucherAmount, locale)}`,
            );
          }
          void log(events.referFriend.SUBMIT_INVITE_TOKEN, {});
        } else {
          // if there's an error validating the referral token we get it through the __typename
          // we should do better error handling on the server and then refactor this
          setSubmitInviteTokenError(response.submitReferralToken?.__typename);
        }
        if (errors != null) {
          // in case of a general server error not related to token validation
          setSubmitInviteTokenError('GeneralError');
          captureMutationErrors(errors);
        }
      },
    });
  }, [submitReferralToken, linkingToken, push, log, signInFulfilled, queryLanguage]);

  useEffect(() => {
    if (signInFulfilled) {
      submitInviteToken();
    }
  }, [signInFulfilled, submitInviteToken]);

  useEffect(() => {
    if (referFriendTokenValidation.isValid) {
      publishRefereeInvitationScreenDisplayed('v2');
    }
  }, []);

  if (isAustralianUser) {
    return <AustralianUserErrorScreen />;
  }

  if (isReferFriendTokenInValid || submitInviteTokenError) {
    return (
      <ReferFriendTokenErrorScreen
        referFriendTokenValidationErrorType={
          submitInviteTokenError ? submitInviteTokenError : referFriendTokenValidationErrorType
        }
        refereeVoucherAmount={formatCurrency(refereeVoucherAmount, locale)}
        maximumRefererCreditTotal={formatCurrency(maximumRefererCreditTotal, locale)}
      />
    );
  }

  if (isGeneralError) {
    void log(events.referFriend.SHOWED_ERROR_SCREEN, { errorType: 'GeneralError' });
    publishRefereeErrorScreenDisplayed('general_error', 'v2');
    return <ErrorScreen />;
  }

  const onOpenSignUp = () => {
    void log(events.referFriend.CLICK_CREATE_ACCOUNT, {});
    onOpenModal({
      initialScreen: 'intro',
      onSignInFulfill: () => {
        setSignInFulfilled(true);
      },
    });
  };

  const inviteePageImage =
    'https://images.skypicker.com/?image=https://images.kiwi.com/account/refer-friend/invitee-page-image.png';
  const inviteePageMobileImage =
    'https://images.skypicker.com/?image=https://images.kiwi.com/account/refer-friend/invitee-page-mobile-image.png';
  const imageLoader = ({ src }) => src;

  if (referFriendTokenValidation.isValid) {
    return (
      <Box
        display="flex"
        direction="column"
        overflow="auto"
        tablet={{ direction: 'row' }}
        className="mx-auto bg-refer-friend-background h-lvh relative w-full"
        data-test="ReferFriendIntro"
      >
        <Box
          tablet={{ margin: { top: '1600' }, maxWidth: '625px' }}
          className="tb:ml-[5%]  ld:ml-[20%] relative z-10"
        >
          <Box
            direction="column"
            display="flex"
            align="center"
            margin={{ top: '600', bottom: 'none' }}
            padding={{ right: '400', left: '400' }}
            tablet={{
              align: 'start',
              margin: { top: 'none', bottom: '1200' },
              padding: { right: 'none', left: 'none' },
            }}
          >
            <Text spaceAfter="medium" size="small" type="primary" weight="bold">
              <FormattedMessage
                id="account_v2.refer_friend.intro_page.header.invitation_message"
                defaultMessage="You've been invited to travel with Kiwi.com"
              />
            </Text>
            <Heading
              spaceAfter="medium"
              type="title1"
              align="center"
              as="h1"
              tablet={{ align: 'start', spaceAfter: 'none' }}
            >
              <FormattedMessage
                id="account_v2.refer_friend.intro_page.header.title"
                defaultMessage="Redeem your {inviteeVoucherAmount} voucher for your first booking with Kiwi.com"
                values={{ inviteeVoucherAmount: formatCurrency(refereeVoucherAmount, locale) }}
              />
            </Heading>
          </Box>
          <Box className="tb:hidden" margin={{ bottom: '600' }}>
            <Image
              unoptimized
              alt=""
              className="mx-auto"
              src={inviteePageMobileImage}
              loader={imageLoader}
              priority
              width={450}
              height={200}
              quality={100}
            />
          </Box>
          <TimelineComponent
            refereeVoucherAmount={formatCurrency(refereeVoucherAmount, locale)}
            maximumRefererCreditTotal={formatCurrency(maximumRefererCreditTotal, locale)}
            firstBookingWithinDays={firstBookingWithinDays}
          />

          <Box
            position="fixed"
            bottom="0"
            left="0"
            right="0"
            padding={{ right: '400', left: '400' }}
            tablet={{
              padding: { right: 'none', left: 'none' },
              position: 'relative',
            }}
            className="bg-refer-friend-background tb:w-[181px]"
          >
            <ButtonPrimitive
              onClick={onOpenSignUp}
              data-test="ReferFriendCreateAccountButton"
              height="44px"
              className="bg-refer-friend-purple hover:bg-refer-friend-purple-hover active:bg-refer-friend-purple-active w-full  text-cloud-normal px-400 text-normal mb-600 tb:mb-0 tb:self-end z-10"
            >
              <FormattedMessage
                id="account_v2.refer_friend.intro_page.button.create_account"
                defaultMessage="Create account"
              />
            </ButtonPrimitive>
          </Box>
        </Box>
        <Box className="hidden tb:block absolute top-1600 right-0 z-0">
          <Image
            unoptimized
            alt=""
            src={inviteePageImage}
            loader={imageLoader}
            priority
            width={600}
            height={280}
            quality={100}
          />
        </Box>
      </Box>
    );
  }

  return null;
};

export default ReferFriendIntro;
